import React, { useState, useEffect } from 'react';

interface PDFBookDisplayProps {
  title: string;
  description?: string;
  pdfUrl: string;
  size?: 'small' | 'medium' | 'large';
}

const PDFBookDisplay: React.FC<PDFBookDisplayProps> = ({ 
  title, 
  description, 
  pdfUrl, 
  size = 'medium' 
}) => {
  const [pdfPreview, setPdfPreview] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Debug du composant
  console.log('📚 PDFBookDisplay rendu:', { title, pdfUrl, size });

  // Générer l'aperçu de la première page du PDF
  useEffect(() => {
    const generatePDFPreview = async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        
        // Méthode 1: Utiliser l'API backend pour générer l'aperçu (temporairement désactivée)
        // try {
        //   console.log('🔄 Tentative API backend pour:', pdfUrl);
        //   const response = await fetch(`http://127.0.0.1:8000/api/pdf-preview/?url=${encodeURIComponent(pdfUrl)}`);
        //   
        //   if (response.ok) {
        //     const data = await response.json();
        //     console.log('📡 Réponse API backend:', data);
        //     if (data.success && data.preview) {
        //       setPdfPreview(data.preview);
        //       console.log(`✅ Aperçu PDF généré via ${data.method}`);
        //       return;
        //     }
        //   } else {
        //     console.warn('❌ API backend response not ok:', response.status, response.statusText);
        //   }
        // } catch (apiError) {
        //   console.warn('❌ API backend failed:', apiError);
        // }
        
        // Méthode 2: Utiliser PDF.js côté client comme fallback
        console.log('🔄 Tentative PDF.js côté client');
        if (typeof window !== 'undefined' && (window as any).pdfjsLib) {
          try {
            console.log('📚 PDF.js disponible, chargement du PDF...');
            const pdfjsLib = (window as any).pdfjsLib;
            
            const loadingTask = pdfjsLib.getDocument({
              url: pdfUrl,
              cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/',
              cMapPacked: true,
            });
            
            console.log('⏳ Chargement du document PDF...');
            const pdf = await loadingTask.promise;
            console.log('📄 PDF chargé, récupération de la page 1...');
            const page = await pdf.getPage(1);
            
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            
            const viewport = page.getViewport({ scale: 1.2 });
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            
            console.log('🎨 Rendu de la page en cours...');
            await page.render({
              canvasContext: context,
              viewport: viewport
            }).promise;
            
            const imageUrl = canvas.toDataURL('image/jpeg', 0.9);
            setPdfPreview(imageUrl);
            console.log('✅ Aperçu PDF généré via PDF.js client');
            return;
            
          } catch (pdfError) {
            console.warn('❌ PDF.js client failed:', pdfError);
          }
        } else {
          console.warn('⚠️ PDF.js non disponible');
        }
        
        // Méthode 3: Utiliser Google Drive Viewer comme aperçu
        try {
          console.log('🔄 Tentative Google Drive Viewer...');
          // Créer une URL d'aperçu via Google Drive Viewer
          const googleViewerUrl = `https://drive.google.com/viewerng/viewer?embedded=true&url=${encodeURIComponent(pdfUrl)}`;
          
          // Pour l'instant, utilisons une capture d'écran simulée
          // En production, on pourrait utiliser un service de capture d'écran
          console.log('📸 URL Google Viewer:', googleViewerUrl);
          
          // Simuler un aperçu avec une iframe cachée (approche alternative)
          // setPdfPreview(googleViewerUrl); // Ne fonctionne pas directement
          
        } catch (externalError) {
          console.warn('❌ Google Viewer failed:', externalError);
        }
        
        // Méthode 4: Fallback SVG stylisé
        const fallbackSvg = `data:image/svg+xml;base64,${btoa(`
          <svg width="200" height="250" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="pageGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" style="stop-color:#ffffff;stop-opacity:1" />
                <stop offset="100%" style="stop-color:#f8f9fa;stop-opacity:1" />
              </linearGradient>
            </defs>
            <rect width="200" height="250" fill="url(#pageGrad)" stroke="#dee2e6" stroke-width="2" rx="4"/>
            <rect x="20" y="30" width="160" height="6" fill="#495057" opacity="0.4" rx="2"/>
            <rect x="20" y="45" width="140" height="6" fill="#495057" opacity="0.4" rx="2"/>
            <rect x="20" y="60" width="160" height="6" fill="#495057" opacity="0.4" rx="2"/>
            <rect x="20" y="75" width="120" height="6" fill="#495057" opacity="0.4" rx="2"/>
            <rect x="20" y="100" width="160" height="6" fill="#6c757d" opacity="0.3" rx="2"/>
            <rect x="20" y="115" width="140" height="6" fill="#6c757d" opacity="0.3" rx="2"/>
            <rect x="20" y="130" width="100" height="6" fill="#6c757d" opacity="0.3" rx="2"/>
            <rect x="150" y="210" width="40" height="20" fill="#dc3545" rx="10"/>
            <text x="170" y="223" text-anchor="middle" fill="white" font-family="Arial" font-size="10" font-weight="bold">PDF</text>
            <path d="M 180 10 L 200 10 L 200 30 Z" fill="#e9ecef" stroke="#dee2e6"/>
          </svg>
        `)}`;
        
        setPdfPreview(fallbackSvg);
        console.log('📄 Utilisation du fallback SVG stylisé (temporaire)');
        
      } catch (error) {
        console.error('Erreur lors de la génération de l\'aperçu PDF:', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    if (pdfUrl) {
      // Délai pour s'assurer que PDF.js est chargé
      setTimeout(() => {
        generatePDFPreview();
      }, 1000);
    }
  }, [pdfUrl]);
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return {
          container: 'w-20 h-24',
          icon: 'w-6 h-6',
          title: 'text-xs',
          badge: 'text-xs px-1 py-0.5',
          pages: 'top-0.5 left-0.5'
        };
      case 'large':
        return {
          container: 'w-36 h-44',
          icon: 'w-16 h-16',
          title: 'text-sm',
          badge: 'text-xs px-2 py-1',
          pages: 'top-2 left-2'
        };
      default: // medium
        return {
          container: 'w-28 h-36',
          icon: 'w-12 h-12',
          title: 'text-xs',
          badge: 'text-xs px-1.5 py-0.5',
          pages: 'top-1 left-1'
        };
    }
  };

  const classes = getSizeClasses();

  return (
    <div className="pdf-book-display w-full flex justify-center">
      <div 
        className="relative group cursor-pointer transform transition-all duration-300 hover:scale-110" 
        onClick={() => {
          console.log('📄 Ouverture PDF:', pdfUrl);
          window.open(pdfUrl, '_blank');
        }}
      >
        {/* Pages arrière (effet de pile) */}
        <div className={`absolute ${classes.pages} ${classes.container} bg-gray-200 rounded-lg shadow-md transform rotate-2 opacity-60`}></div>
        <div className={`absolute top-0.5 left-0.5 ${classes.container} bg-gray-300 rounded-lg shadow-md transform rotate-1 opacity-80`}></div>
        
        {/* Page principale (couverture) */}
        <div className={`relative ${classes.container} bg-white rounded-lg shadow-xl border-2 border-gray-300 overflow-hidden`}>
          {/* Reliure gauche */}
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-red-500 via-red-600 to-red-700"></div>
          
          {/* Couverture */}
          <div className="h-full bg-white flex flex-col relative overflow-hidden">
            {/* Aperçu de la première page du PDF */}
            {isLoading ? (
              <div className="flex-1 flex items-center justify-center bg-gray-100">
                <div className="text-center">
                  <div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-xs text-gray-600">Chargement...</p>
                </div>
              </div>
            ) : hasError || !pdfPreview ? (
              <div className="flex-1 flex flex-col items-center justify-center bg-gradient-to-br from-red-50 via-white to-red-100 p-2">
                {/* Icône PDF de fallback */}
                <div className="mb-2">
                  <svg className={`${classes.icon} text-red-600`} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                    <path d="M10.5,11.5C10.5,12.3 9.8,13 9,13H8V15H6.5V9H9C9.8,9 10.5,9.7 10.5,10.5V11.5M9,10.5H8V11.5H9V10.5M14.5,13.5C14.5,14.3 13.8,15 13,15H11.5V9H13C13.8,9 14.5,9.7 14.5,10.5V13.5M13,10.5H12.5V13.5H13V10.5M18,14H16.5V15H18V16.5H15V9H18V10.5H16.5V12.5H18V14Z" />
                  </svg>
                </div>
              </div>
            ) : (
              <div className="flex-1 relative">
                {/* Image de la première page */}
                <img 
                  src={pdfPreview} 
                  alt={`Aperçu de ${title}`}
                  className="w-full h-full object-cover"
                  style={{ imageRendering: 'crisp-edges' }}
                />
              </div>
            )}
            
            {/* Badge PDF */}
            <div className={`absolute bottom-1 right-1 bg-red-600 text-white ${classes.badge} rounded-full font-bold shadow-lg`}>
              PDF
            </div>
            
            {/* Effet de brillance au survol */}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 transition-opacity duration-300"></div>
            
            {/* Ombre de reliure */}
            <div className="absolute left-1.5 top-0 bottom-0 w-3 bg-gradient-to-r from-black/20 to-transparent"></div>
          </div>
          
          {/* Reflet sur la tranche */}
          <div className="absolute right-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-white/50 to-gray-300/50"></div>
        </div>
        
        {/* Tooltip */}
        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-20 pointer-events-none">
          📖 Cliquer pour ouvrir
        </div>
        
        {/* Ombre portée */}
        <div className="absolute top-2 left-2 w-full h-full bg-black/10 rounded-lg -z-10 blur-sm"></div>
      </div>
    </div>
  );
};

export default PDFBookDisplay;
